import React from 'react';
import { resumeSamples } from '../data/resumeSamples';
import { ExternalLink, Download, FileText, Star, Sparkles } from 'lucide-react';

const Resume = () => {
  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shadow-lg shadow-rose-500/20">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white font-display">Resume Templates</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm pl-[52px] max-w-xl leading-relaxed">
          Stand out to recruiters with professionally designed resume formats tailored for various roles. Download and customize them to fit your profile.
        </p>
      </div>

      {/* Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
        {resumeSamples.map((resume, index) => (
          <div
            key={index}
            className="glass-card-glow group hover:-translate-y-2 transition-all duration-500 flex flex-col h-full overflow-hidden"
          >
            {/* Gradient Accent Bar */}
            <div className={`h-1.5 w-full bg-gradient-to-r ${resume.accent} relative overflow-hidden`}>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" 
                style={{ backgroundSize: '200% 100%' }} 
              />
            </div>

            <div className="p-6 flex flex-col flex-1">
              {/* Popular Badge for first item */}
              {index === 0 && (
                <div className="flex items-center gap-1 text-[11px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 w-fit px-2.5 py-1 rounded-lg border border-amber-200 dark:border-amber-800/50 mb-4">
                  <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                  Most Popular
                </div>
              )}

              {/* Icon */}
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${resume.accent} bg-opacity-10 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-md`}>
                <FileText className="w-6 h-6 text-white" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 font-display">{resume.title}</h3>
              <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 w-fit px-3 py-1 rounded-lg mb-4 border border-indigo-100 dark:border-indigo-800/50">
                {resume.targetRole}
              </p>

              <div className="mt-auto pt-5 flex items-center gap-3">
                <a 
                  href={resume.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-300"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Preview
                </a>
                <a 
                  href={resume.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-1 btn-primary py-2.5 px-4 inline-flex items-center justify-center text-sm gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  Get Template
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="glass-card p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20 flex-shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white font-display">Need a custom template?</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">Our templates are fully customizable. Download and make it yours!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resume;
